import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";
import DropDown from "../Inputs/DropDown";

const Pagination = ( props: { records: number, onPageChange: Function } ) => {
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const perPageOptions = [ 10, 25, 50, 100 ];

  // We want pagination to track correct page when hidden/destroyed
  // Retrieve page info from query params when instantiated OR route changes
  useEffect(() => {
    const { page: p, per_page: pp } = router.query;
    // const p = getParam(router.query, "page");
    // const pp = getParam(router.query, "per_page");

    if(p) {
      setPage(parseInt(p as string))
    }

    if(pp) {
      setPerPage(parseInt(pp as string))
    }
  }, [router.asPath])
  
  const numberOfPages = () => {
    return Math.ceil(props.records / perPage);
  }

  const visiblePages = () => {
    const pages = Array.from(new Array(numberOfPages()), (x, i) => i + 1);

    if(pages.length > 7) {
      if(page > 4) {
        return pages.slice(page - 4, page + 3);
      }

      return pages.slice(0, 7);
    }

    return pages;
  }

  const handlePageChange = (p: number, pp?: number) => {
    window.scrollTo({top: 0, behavior: 'smooth'})
    props.onPageChange({
      page: p,
      perPage: pp || perPage
    })
  }

  return (
    <div className="flex flex-col-reverse justify-center items-center md:items-start md:justify-start md:flex-row w-full">
      <div className="flex w-full justify-center md:justify-start md:w-1/4 min-w-40 items-center">
        <span className="pr-2">
          Per Page:
        </span>
        <DropDown
          containerClassName="w-18"
          fullWidth={false}
          type="line"
          value={perPage}
          options={perPageOptions}
          onOptionSelect={(e) => {
            handlePageChange(1, e);
          }}
        />
      </div>

      <div className="w-full"></div>

      <div className="flex w-full md:w-2/3 lg:w-1/2 max-w-lg items-center">
        <div
          className={`${page === 1 ? "opacity-30" : "opacity-100 cursor-pointer"} pr-4`}
          onClick={() => {
            if(page > 1) {
              handlePageChange(1)
            }
          }}
          onKeyDown={(e) => {
            if(e.key === "Enter") {
              if(page > 1) {
                handlePageChange(1)
              }
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="First Page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" width="14" height="14" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </div>

        <div 
          className={`${page === 1 ? "opacity-30" : "opacity-100 cursor-pointer"} pr-4`}
          onClick={() => {
            if(page > 1) {
              handlePageChange(page - 1)
            }
          }}
          onKeyDown={(e) => {
            if(e.key === "Enter") {
              if(page > 1) {
                handlePageChange(page - 1)
              }
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Navigate to Page ${page - 1}`}
        > 
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" width="14" height="14" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </div>

        <div className="flex w-full items-center justify-center overflow-hidden">
          {(
            visiblePages().map((val) => {
              return (
                <div 
                  key={val}
                  className={`${ page === val ? "opacity-100" : "opacity-30" } select-none cursor-pointer p-2 hover:opacity-100 hover:text-red`}
                  onClick={() => {
                    handlePageChange(val)
                  }}
                  onKeyDown={(e) => {
                    if(e.key === "Enter") {
                      handlePageChange(val)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Navigate to Page"
                >
                  { val }
                </div>
              )
            })
          )}
        </div>

        <div 
          className={`${page === numberOfPages() ? "opacity-30" : "opacity-100 cursor-pointer"} pl-4`}
          onClick={() => {
            if(page < numberOfPages()) {
              handlePageChange(page + 1)
            }
          }}
          onKeyDown={(e) => {
            if(e.key === "Enter") {
              if(page < numberOfPages()) {
                handlePageChange(page + 1)
              }
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Navigate to Page ${page + 1}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" width="14" height="14" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>

        <div
          className={`${page === numberOfPages() ? "opacity-30" : "opacity-100 cursor-pointer"} pl-4`}
          onClick={() => {
            if(page < numberOfPages()) {
              handlePageChange(numberOfPages())
            }
          }}
          onKeyDown={(e) => {
            if(e.key === "Enter") {
              if(page < numberOfPages()) {
                handlePageChange(numberOfPages())
              }
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Last Page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" width="14" height="14" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  )
}

export default Pagination;
